import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { chatHistoryQueryOptions } from "~/features/messages/api";
import { MessageRoomTemplate } from "~/features/messages/MessageRoomTemplate";
import type { Conversation } from "~/features/messages/types";
import { userProfileQueryOptions } from "~/features/profile/api";
import type { UserProfileResponse } from "~/features/profile/types";
import { getAvatarUrl } from "~/lib/imageUrl";
import { queryKeys } from "~/lib/queryKeys";
import { useAuthStore } from "~/stores/useAuthStore";

const MessageRoomPage = () => {
  const { conversationId } = Route.useParams();
  const queryClient = useQueryClient();

  // 会話一覧キャッシュから partnerName を解決（infiniteQuery の pages 構造）
  const cachedData = queryClient.getQueryData<InfiniteData<Conversation[]>>(
    queryKeys.chat.conversations("all"),
  );
  const conversation = cachedData?.pages
    .flat()
    .find((c) => c.partnerId === conversationId);

  // プロフィールキャッシュからも名前を取得（直接URL遷移対応）
  const cachedProfile = queryClient.getQueryData<UserProfileResponse>(
    queryKeys.user.profile(conversationId),
  );

  const partnerName =
    conversation?.partnerName ?? cachedProfile?.user_name ?? conversationId;

  const partnerAvatar =
    conversation?.partnerAvatar ?? getAvatarUrl(cachedProfile?.ava_id ?? null);

  const partnerAge = conversation?.age ?? cachedProfile?.age ?? null;
  const partnerRegion = conversation?.region ?? cachedProfile?.region ?? null;

  return (
    <div className="fixed inset-0 z-40 bg-white">
      <MessageRoomTemplate
        frdId={conversationId}
        partnerName={partnerName}
        partnerAvatar={partnerAvatar}
        partnerAvatarId={cachedProfile?.ava_id ?? null}
        partnerAge={partnerAge}
        partnerRegion={partnerRegion}
        videoCallAllowed={cachedProfile?.video_call_waiting === true}
        voiceCallAllowed={cachedProfile?.voice_call_waiting === true}
      />
    </div>
  );
};

export const Route = createFileRoute(
  "/_authenticated/messages/$conversationId",
)({
  staticData: { hideBottomNav: true },
  loader: ({ context: { queryClient }, params: { conversationId } }) => {
    const userId = useAuthStore.getState().userId ?? "";
    return Promise.all([
      queryClient.ensureQueryData(
        chatHistoryQueryOptions(userId, conversationId),
      ),
      queryClient.ensureQueryData(userProfileQueryOptions(conversationId)),
    ]);
  },
  component: MessageRoomPage,
});
